import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from '@/store/session';
import { DEVICES_POLL_MS, PRESENCE_FALLBACK_MS, PREVIEW_POLL_MS, SESSION_POLL_MS, TASKS_POLL_MS } from '@/lib/constants';
import { logError, logInfo } from '@/lib/log';
import { api } from './endpoints';
import { qk } from './keys';
import type { CorrectionsPatchBody, PipelineSession, Presence, PreviewBody, TaskQuery } from './types';

export const useWs = () => useSession((s) => s.workspaceId);

export const useOverview = () => {
  const w = useWs();
  return useQuery({ queryKey: qk.overview(w), queryFn: api.workspace.overview });
};
export const useProjects = (customerId?: string) => {
  const w = useWs();
  return useQuery({ queryKey: qk.projects(w, customerId), queryFn: () => api.projects.list(customerId) });
};
export const useProject = (id: string | undefined) => {
  const w = useWs();
  return useQuery({ queryKey: qk.project(w, id ?? ''), queryFn: () => api.projects.get(id!), enabled: !!id });
};
export const useCustomers = (enabled = true) => {
  const w = useWs();
  return useQuery({ queryKey: qk.customers(w), queryFn: api.customers.list, enabled });
};
export const useCustomer = (id: string | undefined) => {
  const w = useWs();
  return useQuery({ queryKey: qk.customer(w, id ?? ''), queryFn: () => api.customers.get(id!), enabled: !!id });
};
export const useKitTree = () => {
  const w = useWs();
  return useQuery({ queryKey: qk.kitTree(w), queryFn: api.kits.tree });
};
export const useKit = (id: string | undefined) => {
  const w = useWs();
  return useQuery({ queryKey: qk.kit(w, id ?? ''), queryFn: () => api.kits.get(id!), enabled: !!id });
};
export const useMembers = () => {
  const w = useWs();
  return useQuery({ queryKey: qk.members(w), queryFn: api.team.members });
};
export const useInvites = (enabled: boolean) => {
  const w = useWs();
  return useQuery({ queryKey: qk.invites(w), queryFn: api.team.invites, enabled });
};

// ── White-label (WS J) ───────────────────────────────────────────
/** Custom domains; refreshed every 20 s while one is still waiting for DNS or its certificate. */
export const useDomains = () => {
  const w = useWs();
  return useQuery({
    queryKey: qk.domains(w),
    queryFn: api.domains.list,
    refetchInterval: (q) => (q.state.data?.items.some((d) => d.status === 'pending' || (d.status === 'verified' && d.cert_status !== 'issued')) ? 20_000 : false),
  });
};
export const useBrandingSettings = () => {
  const w = useWs();
  return useQuery({ queryKey: qk.brandingSettings(w), queryFn: api.branding.settings });
};
export const useEmailSender = () => {
  const w = useWs();
  return useQuery({ queryKey: qk.emailSender(w), queryFn: api.email.sender });
};

export const useWorkspace = () => {
  const w = useWs();
  return useQuery({ queryKey: qk.workspace(w), queryFn: api.workspace.get });
};

// ── Devices (amendment 14) ───────────────────────────────────────
/** Studio computers; refreshed every 15 s so Online/Offline and "last seen" stay current. */
export const useDevices = (enabled = true) => {
  const w = useWs();
  return useQuery({ queryKey: qk.devices(w), queryFn: api.devices.list, enabled, refetchInterval: DEVICES_POLL_MS });
};
export const useDeviceFiles = (deviceId: string | undefined) => {
  const w = useWs();
  return useQuery({ queryKey: qk.deviceFiles(w, deviceId ?? ''), queryFn: () => api.devices.files(deviceId!), enabled: !!deviceId });
};

// ── Pipeline ─────────────────────────────────────────────────────
export const useSessions = (episodeId: string | undefined) => {
  const w = useWs();
  return useQuery({ queryKey: qk.sessions(w, episodeId ?? ''), queryFn: () => api.pipeline.sessions(episodeId!), enabled: !!episodeId });
};

const isActive = (s: PipelineSession | undefined) => !!s && (s.status === 'queued' || s.status === 'processing' || s.stages.some((x) => x.status === 'queued' || x.status === 'running'));

/** A Fix & enhance preview is queued or rendering for some source (previews never change the session status). */
export const previewPending = (s: PipelineSession | undefined) => !!s && s.sources.some((x) => x.preview?.status === 'queued' || x.preview?.status === 'running');

/** Session status, polled every 3 s while anything is queued or running (contract §4.5), faster while a preview renders. */
export const usePipelineSession = (sessionId: string | undefined) => {
  const w = useWs();
  return useQuery({
    queryKey: qk.session(w, sessionId ?? ''),
    queryFn: () => api.pipeline.session(sessionId!),
    enabled: !!sessionId,
    refetchInterval: (q) => (previewPending(q.state.data) ? PREVIEW_POLL_MS : isActive(q.state.data) ? SESSION_POLL_MS : false),
  });
};

/**
 * WS F: PATCH a source's correction overrides. Refreshes the session (effective profile, warnings, status turns
 * `processing` when drafts re-render) and the drill-down (renders queued).
 */
export function usePatchCorrections(sessionId: string, episodeId: string) {
  const qc = useQueryClient();
  const w = useWs();
  return useMutation({
    mutationFn: ({ sourceId, body }: { sourceId: string; body: CorrectionsPatchBody }) => {
      logInfo('enhance', `save corrections session=${sessionId} source=${sourceId} body=${JSON.stringify(body)}`);
      return api.pipeline.patchCorrections(sessionId, sourceId, body);
    },
    onSuccess: async (res, { sourceId }) => {
      logInfo('enhance', `saved corrections session=${sessionId} source=${sourceId} origin=${res.origin} rerendered=${res.rerendered.length} warnings=${res.warnings.length}`);
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.session(w, sessionId) }),
        qc.invalidateQueries({ queryKey: qk.drilldown(w, episodeId) }),
      ]);
    },
    onError: (e, { sourceId }) => logError('enhance', `save corrections failed session=${sessionId} source=${sourceId}`, e),
  });
}

/** WS F: queue a before/after preview render; the session query then polls until `preview.status` settles. */
export function useRequestPreview(sessionId: string) {
  const qc = useQueryClient();
  const w = useWs();
  return useMutation({
    mutationFn: ({ sourceId, body }: { sourceId: string; body?: PreviewBody }) => {
      logInfo('enhance', `request preview session=${sessionId} source=${sourceId} ${JSON.stringify(body ?? {})}`);
      return api.pipeline.preview(sessionId, sourceId, body);
    },
    onSuccess: async (res, { sourceId }) => {
      logInfo('enhance', `preview queued session=${sessionId} source=${sourceId} job=${res.job_id}`);
      await qc.invalidateQueries({ queryKey: qk.session(w, sessionId) });
    },
    onError: (e, { sourceId }) => logError('enhance', `preview request failed session=${sessionId} source=${sourceId}`, e),
  });
}

export const useTranscript = (sessionId: string | undefined, enabled = true) => {
  const w = useWs();
  return useQuery({ queryKey: qk.transcript(w, sessionId ?? ''), queryFn: () => api.pipeline.transcript(sessionId!), enabled: !!sessionId && enabled });
};

/**
 * Drill-down tree. Polls while a render is queued/rendering, or while the session is busy
 * (e.g. a regenerate job is re-planning a tier), so new nodes and files appear by themselves.
 */
export const useDrilldown = (episodeId: string | undefined, enabled = true, sessionBusy = false) => {
  const w = useWs();
  return useQuery({
    queryKey: qk.drilldown(w, episodeId ?? ''),
    queryFn: () => api.pipeline.drilldown(episodeId!),
    enabled: !!episodeId && enabled,
    refetchInterval: (q) => {
      if (sessionBusy) return SESSION_POLL_MS;
      const d = q.state.data;
      if (!d) return false;
      const all = [d.full, ...d.value, ...d.social].filter((n): n is NonNullable<typeof n> => !!n);
      return all.some((n) => n.render && (n.render.status === 'queued' || n.render.status === 'rendering')) ? SESSION_POLL_MS : false;
    },
  });
};

/** Session is queued or running something (initial pipeline, regenerate, re-render). */
export const isSessionBusy = (s: PipelineSession | undefined) => isActive(s);

/** Mutation that invalidates the whole workspace cache on success (small app; correctness over precision). */
export function useWsMutation<TVars, TData>(fn: (v: TVars) => Promise<TData>, opts: { onSuccess?: (d: TData, v: TVars) => void } = {}) {
  const qc = useQueryClient();
  const w = useWs();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (d, v) => {
      opts.onSuccess?.(d, v);
      await qc.invalidateQueries({ queryKey: qk.ws(w) });
    },
  });
}

// ── Tasks, activity feed and presence (plan §19.4) ──────────────
export const useTasks = (q: TaskQuery, enabled = true) => {
  const w = useWs();
  return useQuery({ queryKey: qk.tasks(w, q as Record<string, unknown>), queryFn: () => api.tasks.list(q), enabled, refetchInterval: TASKS_POLL_MS });
};
export const useTask = (id: string | undefined) => {
  const w = useWs();
  return useQuery({ queryKey: qk.task(w, id ?? ''), queryFn: () => api.tasks.get(id!), enabled: !!id });
};
export const useTaskSettings = (enabled = true) => {
  const w = useWs();
  return useQuery({ queryKey: qk.taskSettings(w), queryFn: api.tasks.settings, enabled });
};
export const useTaskAssignees = (projectId: string | undefined, enabled = true) => {
  const w = useWs();
  return useQuery({ queryKey: qk.taskAssignees(w, projectId ?? ''), queryFn: () => api.tasks.assignees(projectId!), enabled: !!projectId && enabled });
};
export const useTaskEmail = () => {
  const w = useWs();
  return useQuery({ queryKey: qk.taskEmail(w), queryFn: api.tasks.email });
};
export const useActivity = (q: { project_id?: string; episode_id?: string; limit?: number }, enabled = true) => {
  const w = useWs();
  return useQuery({ queryKey: qk.activity(w, q), queryFn: () => api.activity.feed(q), enabled, refetchInterval: TASKS_POLL_MS });
};

/**
 * Presence on an episode (plan §19.4 item 7): heartbeats every `heartbeat_s` (10 s) with the video being edited, and
 * leaves on unmount. The API keeps it in Redis with a short TTL, so a closed tab disappears by itself.
 */
export function usePresence(episodeId: string | undefined, editingEditId: string | null, enabled = true) {
  const [presence, setPresence] = useState<Presence | null>(null);
  const editing = useRef(editingEditId);
  editing.current = editingEditId;
  const beatNow = useRef<() => void>(() => undefined);
  useEffect(() => {
    if (!episodeId || !enabled) return;
    let stop = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const beat = async () => {
      if (timer) clearTimeout(timer);
      let next = PRESENCE_FALLBACK_MS;
      try {
        const p = await api.presence.beat(episodeId, editing.current);
        if (stop) return;
        setPresence(p);
        next = p.heartbeat_s * 1000;
        if (p.conflicts.length) logInfo('presence', `conflict episode=${episodeId} edit=${editing.current} with=${p.conflicts.map((c) => c.member_id).join(',')}`);
      } catch (e) {
        logError('presence', `heartbeat failed episode=${episodeId}`, e);
      }
      if (!stop) timer = setTimeout(() => void beat(), next);
    };
    beatNow.current = () => void beat();
    void beat();
    return () => {
      stop = true;
      if (timer) clearTimeout(timer);
      beatNow.current = () => undefined;
      api.presence.leave(episodeId).catch((e) => logError('presence', `leave failed episode=${episodeId}`, e));
    };
  }, [episodeId, enabled]);
  // Tell the others at once when the video being edited changes (the loop above already beat on mount).
  const lastEditing = useRef(editingEditId);
  useEffect(() => {
    if (lastEditing.current === editingEditId) return;
    lastEditing.current = editingEditId;
    beatNow.current();
  }, [editingEditId]);
  return presence;
}
